import React, { useEffect, useContext, useState } from "react";
import styled from "styled-components";
import Header from "../components/OrderPayment/Header";
import OrderSummary from "../components/OrderPayment/OrderSummary";
import DeliveryAddress, { DeliveryInfo } from "../components/OrderPayment/DeliveryAddress";
import PayMethod from "../components/OrderPayment/PayMethod";
import Footer from "../components/OrderPayment/Footer";
import { useDelayedNavigation } from "../useDelayedNavigation";
import { ProductContext } from "../context/ProductContext";
import { useParams } from "react-router-dom";

const initialDeliveryInfo: DeliveryInfo = {
  recipientName: "",
  postcode: "",
  address: "",
  addressDetail: "",
  phone1: "010",
  phone2: "",
  phone3: "",
};

const OrderPaymentPage: React.FC = () => {
  const delayedNavigate = useDelayedNavigation();
  const context = useContext(ProductContext);
  const { product_id } = useParams<{ product_id: string }>();
  const [deliveryInfo, setDeliveryInfo] = useState<DeliveryInfo>(initialDeliveryInfo);

  if (!context) {
    throw new Error("ProductContext를 찾을 수 없습니다.");
  }

  const { isLoading, loadProductData } = context;

  useEffect(() => {
    const existingMeta = document.querySelector('meta[name="viewport"]');
    if (existingMeta) {
      existingMeta.remove();
    }

    const meta = document.createElement("meta");
    meta.name = "viewport";
    meta.content =
      "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0";
    document.head.appendChild(meta);

    return () => {
      if (meta) {
        meta.remove();
      }
    };
  }, []);

  // 주문 페이지 진입 시에도 상품/은행 정보를 보장적으로 로드
  useEffect(() => {
    if (product_id) {
      loadProductData(product_id).catch((e) => {
        console.error("주문 페이지 상품 로드 실패:", e);
      });
    }
  }, [product_id, loadProductData]);

  const handlePaymentMethodChange = (method: string) => {
    console.log("Payment method selected:", method, "Product ID:", product_id);
    if (method === "safe-payment") {
      if (product_id) {
        delayedNavigate(`/product/${product_id}/order-complete`);
      } else {
        console.error(
          "Product ID가 정의되어 있지 않습니다. 주문 완료 페이지로 이동할 수 없습니다."
        );
        alert("상품 정보가 없습니다.");
      }
    }
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
      </LoadingContainer>
    );
  }

  return (
    <ContainerWrapper>
      <PageWrapper>
        <ScaledContent>
          <Header />
          <main>
            <OrderSummary />
            <DeliveryAddress value={deliveryInfo} onChange={setDeliveryInfo} />
            <PayMethod
              onPaymentMethodChange={handlePaymentMethodChange}
              productId={product_id || ""}
              deliveryInfo={deliveryInfo}
            />
          </main>
          <Footer />
        </ScaledContent>
      </PageWrapper>
    </ContainerWrapper>
  );
};

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: #f4f5f7;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #27c360;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const ContainerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 1200px);
  background-color: #f4f5f7;
  overflow-x: hidden;
`;

const PageWrapper = styled.div`
  background-color: #f4f5f7;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 864px;
  margin: 0 auto;
  padding: 20px;
  overflow: hidden;
`;

const ScaledContent = styled.div`
  transform-origin: top center;
  @media (max-width: 864px) {
    transform: scale(0.47619);
    width: 210%;
    margin-left: -55%;
  }
`;

export default OrderPaymentPage;
